import config from './config';

/** The providers for managing tokens. */
export enum AuthTokenHandlerProvider {
  None = 'none',
  SFCC = 'sfcc'
}
/**
 * Authentication configuration for cookie-based token storage.
 */
const auth = config({
  /** Defaults for all sites and locales. */
  default: {
    /** Default settings for all languages. */
    default: {
      /** Cookie configuration for auth tokens. */
      cookies: {
        /** Prefix for all auth cookie names. */
        prefix: 'auth_',

        /**
         * Cookie expiration time in seconds.
         * Default: 7 days for long-lived sessions.
         */
        maxAge: 7 * 24 * 60 * 60,

        /**
         * Cookie path scope.
         * Default: '/' to make cookies available site-wide.
         */
        path: '/'
      },

      /**
       * Buffer time in seconds before token expiration to trigger a refresh.
       * Refreshing early prevents edge cases where token expires mid-request.
       * Default: 60 seconds.
       */
      tokenRefreshBuffer: 60,

      /**
       * Whether the auth adapter invalidates all tokens after password change,
       * requiring the user to re-authenticate.
       *
       * **SFCC/SLAS**: Access tokens are revoked after password change. Users
       * must be prompted to log in again with their new credentials.
       * @see https://developer.salesforce.com/docs/commerce/commerce-api/guide/slas.html#access-tokens-and-refresh-tokens
       *
       * **Cognito**: Tokens remain valid after password change. No
       * re-authentication required.
       *
       * **Note:** This setting must align with the auth adapter configured in
       * the DI container (IAuthServiceAdapter). See
       * libs/services/src/dependency-injection/container/index.ts
       */
      requiresReloginAfterPasswordChange: true,

      /**
       * Whether to automatically re-authenticate the user after a password change
       * (when `requiresReloginAfterPasswordChange` is true).
       *
       * When enabled, the server re-authenticates with the new password during
       * the password change request, avoiding the need for the user to manually
       * log in again. The password never leaves the server context.
       */
      enableAutoLoginAfterPasswordChange: true,
      /** The provider to manage the user tokens. */
      tokenHandlerProvider:
        AuthTokenHandlerProvider.None as AuthTokenHandlerProvider
    }
  },

  'AHNU-US': {
    default: {
      /**
       * Cognito does not invalidate tokens after password change.
       * No re-authentication required.
       */
      requiresReloginAfterPasswordChange: false
    }
  },
  'TEVA-US': {
    default: { tokenHandlerProvider: AuthTokenHandlerProvider.SFCC }
  }
});

export default auth;
